import { toast } from "sonner";
import { useState, useEffect, useRef } from "react";
import { apiService, type Prompt, type PromptVersion, type SlotAssignment } from "../../services/api.service";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PlusCircle, Trash2 } from "lucide-react";

interface PromptInput {
  prompt_id: string;
  version: number;
  model: string;
  llm_config_name: string;
  slot_assignments?: SlotAssignment[];
}

interface LLMConfigEntry {
  name: string;
  model: string;
  temperature: number | null;
  max_tokens: number | null;
  extra: { key: string; value: string }[];
}

interface FormData {
  prompts: PromptInput[];
  llm_configs: LLMConfigEntry[];
  [key: string]: unknown;
}

interface SlotsCtx {
  availablePrompts: Prompt[];
  loadVersionsForPrompt: (promptId: string, explicitAppId?: string) => Promise<void>;
  explicitAppId?: string;
  getPromptMeta: (promptId: string) => Prompt | undefined;
  /** Selectable version numbers — fetched list if loaded, else derived from
   * prompt metadata (1..latest_version). Fetch happens lazily on dropdown open. */
  getVersionOptions: (promptId: string, current: number) => number[];
  getPromptInput: (promptId: string) => PromptInput | undefined;
  updatePromptSlots: (promptId: string, slots: SlotAssignment[]) => void;
  /** Create a flat form entry for a prompt newly referenced as a slot option,
   * so its own placeholders become editable (recursively) and it gets saved. */
  ensurePromptInput: (promptId: string) => void;
  /** Keep the flat form entry's version in sync with the slot option's version. */
  setPromptInputVersion: (promptId: string, version: number) => void;
  /** Remove an auto-created flat entry once nothing references it anymore —
   * otherwise it would surface as a stray top-level row and be saved into
   * the tutor version. Cascades to auto-created children. */
  releasePromptInput: (promptId: string) => void;
}

interface SlotsEditorProps {
  /** Prompt whose placeholders are being edited at this level. */
  ownerPromptId: string;
  slots: SlotAssignment[];
  onChange: (slots: SlotAssignment[]) => void;
  /** Unique prefix for radio group names, extended per recursion level. */
  radioPrefix: string;
  depth: number;
  /** Prompt ids already being edited up the chain — blocks cyclic recursion. */
  visited: string[];
  ctx: SlotsCtx;
}

// A pinned prompt missing from the list (e.g. hidden) still shows as itself.
const UnlistedPromptItem = ({ prompts, promptId }: { prompts: Prompt[]; promptId?: string }) =>
  promptId && !prompts.some((p) => p.prompt_id === promptId) ? (
    <SelectItem value={promptId}>{promptId} (not listed)</SelectItem>
  ) : null;

// A config name outside the generic list (e.g. `notepad`, now in the Notepad Models card) still shows.
const UnlistedConfigItem = ({ configs, name }: { configs: { name: string }[]; name?: string }) =>
  name && !configs.some((c) => c.name === name) ? <SelectItem value={name}>{name} (not listed)</SelectItem> : null;

// Recursive placeholder slot editor. Rows are driven by the prompt's metadata
// (so newly added placeholders always get a slot); assignments whose placeholder
// no longer exists in metadata stay visible/editable. Any option that references
// a prompt present in the form recurses into that prompt's own placeholders,
// at arbitrary depth.
const SlotsEditor = ({
  ownerPromptId,
  slots,
  onChange,
  radioPrefix,
  depth,
  visited,
  ctx,
}: SlotsEditorProps) => {
  const meta = ctx.getPromptMeta(ownerPromptId);
  const metaPlaceholders = meta?.placeholders ?? [];
  const metaNames = new Set(metaPlaceholders.map((mp) => mp.name));
  const entries = [
    ...metaPlaceholders.map((mp) => ({ ...mp, stale: false })),
    ...slots
      .filter((s) => !metaNames.has(s.placeholder_name))
      .map((s) => ({
        name: s.placeholder_name,
        description: "",
        required: false,
        stale: metaPlaceholders.length > 0,
      })),
  ];
  if (entries.length === 0) return null;

  const nextVisited = [...visited, ownerPromptId];

  return (
    <>
      {entries.map((ph) => {
        const slot = slots.find((s) => s.placeholder_name === ph.name);
        const slotOptions = slot?.options ?? [];
        const updateThisSlot = (options: SlotAssignment["options"]) => {
          const exists = slots.some((s) => s.placeholder_name === ph.name);
          onChange(
            exists
              ? slots.map((s) => (s.placeholder_name === ph.name ? { ...s, options } : s))
              : [...slots, { placeholder_name: ph.name, options }],
          );
        };

        return (
          <div key={ph.name} className={`rounded border p-2 space-y-2 ${depth === 0 ? "bg-background" : "bg-muted/20"}`}>
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono font-medium">{`{{${ph.name}}}`}</span>
              {ph.description && (
                <span className="text-xs text-muted-foreground">— {ph.description}</span>
              )}
              {ph.required && (
                <Badge variant="outline" className="text-[10px] px-1 py-0">required</Badge>
              )}
              {ph.stale && (
                <Badge variant="secondary" className="text-[10px] px-1 py-0">not in prompt metadata</Badge>
              )}
            </div>
            <div className="space-y-1 pl-2">
              {slotOptions.map((opt, oi) => {
                const isRuntime = opt.runtime_variable === true;
                return (
                  <div key={oi} className="flex flex-wrap items-center gap-2">
                    <Select
                      value={isRuntime ? "__runtime__" : (opt.prompt_id || "none")}
                      onValueChange={async (val) => {
                        const previousId = opt.prompt_id;
                        if (val === "__runtime__") {
                          const updated = slotOptions.map((o, j) =>
                            j === oi ? { runtime_variable: true as const, default: o.default } : o
                          );
                          updateThisSlot(updated);
                          if (previousId) ctx.releasePromptInput(previousId);
                          return;
                        }
                        const nextId = val === "none" ? "" : val;
                        const updated = slotOptions.map((o, j) =>
                          j === oi ? { prompt_id: nextId, version: 1, default: o.default } : o
                        );
                        updateThisSlot(updated);
                        if (previousId && previousId !== nextId) ctx.releasePromptInput(previousId);
                        if (nextId) {
                          ctx.ensurePromptInput(nextId);
                          await ctx.loadVersionsForPrompt(nextId, ctx.explicitAppId);
                        }
                      }}
                    >
                      <SelectTrigger className="h-7 text-xs w-40">
                        <SelectValue placeholder="prompt" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Select prompt</SelectItem>
                        <SelectItem value="__runtime__">Runtime variable</SelectItem>
                        <UnlistedPromptItem prompts={ctx.availablePrompts} promptId={opt.prompt_id} />
                        {ctx.availablePrompts.map((p) => (
                          <SelectItem key={p.prompt_id} value={p.prompt_id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isRuntime ? (
                      <Badge variant="secondary" className="text-[10px] px-2 py-0.5">filled at runtime</Badge>
                    ) : (
                      <Select
                        value={String(opt.version ?? 1)}
                        onOpenChange={(open) => {
                          if (open && opt.prompt_id) ctx.loadVersionsForPrompt(opt.prompt_id, ctx.explicitAppId);
                        }}
                        onValueChange={(v) => {
                          const nextVersion = Number.parseInt(v, 10) || 1;
                          const updated = slotOptions.map((o, j) =>
                            j === oi ? { ...o, version: nextVersion } : o
                          );
                          updateThisSlot(updated);
                          if (opt.prompt_id) ctx.setPromptInputVersion(opt.prompt_id, nextVersion);
                        }}
                      >
                        <SelectTrigger className="h-7 text-xs w-20">
                          <SelectValue placeholder="v" />
                        </SelectTrigger>
                        <SelectContent>
                          {ctx.getVersionOptions(opt.prompt_id ?? "", opt.version ?? 1).map((v) => (
                            <SelectItem key={v} value={String(v)}>v{v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <label className="flex items-center gap-1 text-xs">
                      <input
                        type="radio"
                        name={`${radioPrefix}-${ph.name}`}
                        checked={opt.default ?? false}
                        onChange={() => {
                          const updated = slotOptions.map((o, j) =>
                            ({ ...o, default: j === oi })
                          );
                          updateThisSlot(updated);
                        }}
                        className="h-3 w-3"
                      />
                      default
                    </label>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => {
                        updateThisSlot(slotOptions.filter((_, j) => j !== oi));
                        if (opt.prompt_id) ctx.releasePromptInput(opt.prompt_id);
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                );
              })}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => updateThisSlot([...slotOptions, { prompt_id: "", version: 1 }])}
                >
                  <PlusCircle className="h-3 w-3 mr-1" />
                  Add prompt option
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => updateThisSlot([...slotOptions, { runtime_variable: true as const }])}
                >
                  <PlusCircle className="h-3 w-3 mr-1" />
                  Add runtime variable
                </Button>
              </div>
            </div>

            {slotOptions
              .filter((o) => o.prompt_id && !nextVisited.includes(o.prompt_id) && ctx.getPromptInput(o.prompt_id))
              .map((o) => {
                const childId = o.prompt_id!;
                const childInput = ctx.getPromptInput(childId)!;
                const childMeta = ctx.getPromptMeta(childId);
                return (
                  <div key={`${ph.name}-${childId}-slots`} className="ml-4 mt-2 space-y-2 border-l-2 border-muted-foreground/20 pl-3">
                    <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                      {childMeta?.name || childId} placeholders
                    </span>
                    <SlotsEditor
                      ownerPromptId={childId}
                      slots={childInput.slot_assignments ?? []}
                      onChange={(s) => ctx.updatePromptSlots(childId, s)}
                      radioPrefix={`${radioPrefix}-${childId}`}
                      depth={depth + 1}
                      visited={nextVisited}
                      ctx={ctx}
                    />
                  </div>
                );
              })}
          </div>
        );
      })}
    </>
  );
};

interface PromptsCardProps {
  title: string;
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  availablePrompts: Prompt[];
  promptVersionsMap: Record<string, PromptVersion[]>;
  loadVersionsForPrompt: (promptId: string, explicitAppId?: string) => Promise<void>;
  getPromptAppId: (promptId: string) => string;
  filterAppId: string;
  /** Rows are grouped by the first matching id prefix; the rest go under "Other". */
  groups?: { label: string; prefix: string }[];
}

export const PromptsCard = ({
  title,
  formData,
  setFormData,
  availablePrompts,
  promptVersionsMap,
  loadVersionsForPrompt,
  getPromptAppId,
  filterAppId,
  groups,
}: PromptsCardProps) => {
  const explicitAppId = filterAppId === "gateway" ? "gateway" : undefined;

  // availablePrompts comes from the list endpoint which may omit `placeholders`.
  // Fetch full prompt details for prompts in the form that have no placeholders loaded.
  const [enrichedPrompts, setEnrichedPrompts] = useState(() => new Map<string, Prompt>());
  const formPromptIds = formData.prompts.map((p) => p.prompt_id).join(",");
  useEffect(() => {
    // Enrich all prompts in the form that are in this card's availablePrompts list
    // but have no placeholders returned by the list endpoint.
    // We check membership in availablePrompts (not getPromptAppId) to avoid stale
    // closure bugs — getPromptAppId depends on allAvailablePrompts from the parent
    // which may not be populated yet when this effect fires.
    const missing = formData.prompts
      .map((p) => p.prompt_id)
      .filter((id) => {
        if (!id || enrichedPrompts.has(id)) return false;
        const meta = availablePrompts.find((p) => p.prompt_id === id);
        return meta !== undefined && !meta.placeholders?.length;
      });
    if (missing.length === 0) return;
    Promise.all(
      missing.map((id) => {
        const appId = availablePrompts.find((p) => p.prompt_id === id)?.application_id ?? filterAppId;
        return apiService.getPrompt(appId, id).catch((error) => { toast.error(error instanceof Error ? error.message : `Failed to load prompt ${id}`); return null; });
      })
    ).then((results) => {
      setEnrichedPrompts((prev) => {
        const next = new Map(prev);
        for (const p of results) {
          if (p) next.set(p.prompt_id, p);
        }
        return next;
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formPromptIds, availablePrompts]);

  const getPromptMeta = (promptId: string): Prompt | undefined =>
    enrichedPrompts.get(promptId) ?? availablePrompts.find((p) => p.prompt_id === promptId);

  // Selectable version numbers without hitting the network: prefer the fetched
  // list, else 1..latest_version from prompt metadata (already loaded with the
  // prompt list), else the currently selected version. The fetched list is
  // loaded lazily when a version dropdown is opened.
  const getVersionOptions = (promptId: string, current: number): number[] => {
    const fetched = promptVersionsMap[promptId];
    if (fetched?.length) return fetched.map((v) => Number.parseInt(v.version, 10));
    const latest = Number.parseInt(getPromptMeta(promptId)?.latest_version ?? "", 10);
    const max = Math.max(1, Number.isNaN(latest) ? 0 : latest, current || 1);
    return Array.from({ length: max }, (_, i) => i + 1);
  };

  const getPromptInput = (promptId: string): PromptInput | undefined =>
    formData.prompts.find(
      (p) => p.prompt_id === promptId && getPromptAppId(p.prompt_id) === filterAppId,
    );

  const updatePromptSlots = (promptId: string, slots: SlotAssignment[]) =>
    setFormData((prev: FormData) => ({
      ...prev,
      prompts: prev.prompts.map((p) =>
        p.prompt_id === promptId ? { ...p, slot_assignments: slots } : p,
      ),
    }));

  // Prompt ids whose flat entries were auto-created by ensurePromptInput (as
  // opposed to user-added rows or entries copied from an existing TV) — only
  // these are eligible for automatic removal when de-referenced.
  const autoAddedRef = useRef<Set<string>>(new Set());

  const ensurePromptInput = (promptId: string) =>
    setFormData((prev: FormData) => {
      if (prev.prompts.some((p) => p.prompt_id === promptId)) return prev;
      autoAddedRef.current.add(promptId);
      return {
        ...prev,
        prompts: [...prev.prompts, { prompt_id: promptId, version: 1, model: "", llm_config_name: "" }],
      };
    });

  const releasePromptInput = (promptId: string) =>
    setFormData((prev: FormData) => {
      let prompts = prev.prompts;
      const queue = [promptId];
      while (queue.length > 0) {
        const id = queue.shift()!;
        if (!autoAddedRef.current.has(id)) continue;
        const stillReferenced = prompts.some(
          (p) =>
            p.prompt_id !== id &&
            (p.slot_assignments ?? []).some((s) => s.options.some((o) => o.prompt_id === id)),
        );
        if (stillReferenced) continue;
        const entry = prompts.find((p) => p.prompt_id === id);
        if (!entry) continue;
        prompts = prompts.filter((p) => p.prompt_id !== id);
        autoAddedRef.current.delete(id);
        // Releasing this entry drops its slot_assignments — cascade to any
        // auto-created children it was the last reference for.
        for (const s of entry.slot_assignments ?? []) {
          for (const o of s.options) {
            if (o.prompt_id) queue.push(o.prompt_id);
          }
        }
      }
      return prompts === prev.prompts ? prev : { ...prev, prompts };
    });

  const setPromptInputVersion = (promptId: string, version: number) =>
    setFormData((prev: FormData) => ({
      ...prev,
      prompts: prev.prompts.some((p) => p.prompt_id === promptId)
        ? prev.prompts.map((p) => (p.prompt_id === promptId ? { ...p, version } : p))
        : [...prev.prompts, { prompt_id: promptId, version, model: "", llm_config_name: "" }],
    }));

  const slotsCtx: SlotsCtx = {
    availablePrompts,
    loadVersionsForPrompt,
    explicitAppId,
    getPromptMeta,
    getVersionOptions,
    getPromptInput,
    updatePromptSlots,
    ensurePromptInput,
    setPromptInputVersion,
    releasePromptInput,
  };

  return (
    <Card className="space-y-3 p-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium">{title}</h4>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const existing = new Set(formData.prompts.map((p) => p.prompt_id));
              const newPrompts = availablePrompts
                .filter((p) => !existing.has(p.prompt_id))
                .map((p) => ({
                  prompt_id: p.prompt_id,
                  version: Number.parseInt(p.active_version, 10) || 1,
                  model: "",
                  llm_config_name: "",
                }));
              setFormData((prev: FormData) => ({ ...prev, prompts: [...prev.prompts, ...newPrompts] }));
            }}
          >
            Add All
          </Button>
          <Button
            size="sm"
            onClick={() =>
              setFormData((p: FormData) => ({
                ...p,
                prompts: [...p.prompts, { prompt_id: "", version: 1, model: "", llm_config_name: "" }],
              }))
            }
          >
            Add Prompt
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {(() => {
          const slotOptionIds = new Set<string>();
          for (const p of formData.prompts) {
            if (!p.prompt_id || getPromptAppId(p.prompt_id) !== filterAppId) continue;
            for (const slot of p.slot_assignments ?? []) {
              for (const opt of slot.options) {
                if (opt.prompt_id) slotOptionIds.add(opt.prompt_id);
              }
            }
          }
          const rows = formData.prompts
            .map((promptInput, index) => ({ promptInput, index }))
            .filter(({ promptInput }) =>
              promptInput.prompt_id &&
              getPromptAppId(promptInput.prompt_id) === filterAppId &&
              !slotOptionIds.has(promptInput.prompt_id),
            );
          const groupOf = (promptId: string) =>
            groups?.find((g) => promptId.startsWith(g.prefix))?.label ?? "Other";
          const sections = groups
            ? [...groups.map((g) => g.label), "Other"]
                .map((label) => ({
                  label,
                  rows: rows.filter((r) => groupOf(r.promptInput.prompt_id) === label),
                }))
                .filter((section) => section.rows.length > 0)
            : [{ label: "", rows }];
          return sections.map((section) => (
            <div key={section.label} className="space-y-2">
              {section.label && (
                <h5 className="pt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {section.label}
                </h5>
              )}
              {section.rows.map(({ promptInput, index }) => (
                <div key={index} className="rounded-md border p-3 space-y-2">
                  <div className="flex flex-wrap items-start gap-2">
                    <div className="min-w-0 flex-[2_1_180px]">
                      <Select
                        value={promptInput.prompt_id || "none"}
                        onValueChange={async (value) => {
                          const nextPromptId = value === "none" ? "" : value;
                          setFormData((prev: FormData) => ({
                            ...prev,
                            prompts: prev.prompts.map((p, i) =>
                              i === index ? { ...p, prompt_id: nextPromptId } : p,
                            ),
                          }));
                          if (nextPromptId) await loadVersionsForPrompt(nextPromptId, explicitAppId);
                        }}
                      >
                        <SelectTrigger className="w-full truncate">
                          <SelectValue placeholder="Select prompt" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Choose a prompt</SelectItem>
                          <UnlistedPromptItem prompts={availablePrompts} promptId={promptInput.prompt_id} />
                          {availablePrompts.map((p) => (
                            <SelectItem key={p.prompt_id} value={p.prompt_id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="min-w-0 flex-[1_1_100px]">
                      <Select
                        value={String(promptInput.version)}
                        onOpenChange={(open) => {
                          if (open && promptInput.prompt_id) loadVersionsForPrompt(promptInput.prompt_id, explicitAppId);
                        }}
                        onValueChange={(value) =>
                          setFormData((prev: FormData) => ({
                            ...prev,
                            prompts: prev.prompts.map((p, i) =>
                              i === index ? { ...p, version: Number.parseInt(value, 10) || 1 } : p,
                            ),
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Version" />
                        </SelectTrigger>
                        <SelectContent>
                          {getVersionOptions(promptInput.prompt_id, promptInput.version).map((v) => (
                            <SelectItem key={v} value={String(v)}>
                              v{v}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="min-w-0 flex-[1_1_120px]">
                      <Select
                        value={promptInput.llm_config_name || "default"}
                        onValueChange={(value) =>
                          setFormData((prev: FormData) => ({
                            ...prev,
                            prompts: prev.prompts.map((p, i) =>
                              i === index ? { ...p, llm_config_name: value === "default" ? "" : value } : p,
                            ),
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="LLM Config" />
                        </SelectTrigger>
                        <SelectContent>
                          <UnlistedConfigItem configs={formData.llm_configs} name={promptInput.llm_config_name} />
                          {formData.llm_configs.map((c) => (
                            <SelectItem key={c.name} value={c.name}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex-none">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setFormData((prev: FormData) => ({
                            ...prev,
                            prompts: prev.prompts.filter((_: PromptInput, i: number) => i !== index),
                          }));
                          // Release any auto-created slot option entries this row
                          // was the last reference for.
                          for (const slot of promptInput.slot_assignments ?? []) {
                            for (const opt of slot.options) {
                              if (opt.prompt_id) releasePromptInput(opt.prompt_id);
                            }
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {(() => {
                    const promptMeta = getPromptMeta(promptInput.prompt_id);
                    const currentSlots = promptInput.slot_assignments ?? [];
                    if ((promptMeta?.placeholders?.length ?? 0) === 0 && currentSlots.length === 0) return null;
                    return (
                      <div className="mt-2 rounded border bg-muted/30 p-3 space-y-3">
                        <span className="text-xs font-medium text-muted-foreground">Placeholders</span>
                        <SlotsEditor
                          ownerPromptId={promptInput.prompt_id}
                          slots={currentSlots}
                          onChange={(slots) =>
                            setFormData((prev: FormData) => ({
                              ...prev,
                              prompts: prev.prompts.map((p, i) =>
                                i === index ? { ...p, slot_assignments: slots } : p,
                              ),
                            }))
                          }
                          radioPrefix={`slot-default-${index}`}
                          depth={0}
                          visited={[]}
                          ctx={slotsCtx}
                        />
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          ));
        })()}
      </div>
    </Card>
  );
};
